// The repo-wide reviews read (arc 101 slice 06) — `GET /v1/repos/:owner/:repo/reviews`.
//
// A SECOND CAPABILITY, not a second envelope over `useReviewRuns` (`./pr-detail.ts`).
// That one answers ONE pull request off the task row: id, status, head sha, base ref.
// The Reviews tab asks "what has the reviewer been doing on this repo, and was it any
// good", and the facts that answer it — which model ran, how many findings it KEPT,
// whether it ran blind — are folded from `task_run_events` by the API. Two questions,
// two shapes, two hooks.
//
// A FIXED HEAD: the route publishes no page size and no cursor, and any query
// parameter is a 400 (`apps/api/src/routes/reviews.ts`). So this hook sends none,
// and the tab says on screen that it is showing the newest runs only rather than
// implying the list is the whole history.

import { TaskStatusSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { repoApiBase } from './repo-detail';
import { SESSION_TOKEN } from './session';

/**
 * WHETHER A MODEL RAN, AS A VERDICT — three states, never a boolean.
 *
 * - `model`: a credential resolved and the brain reviewed the diff. `provider` /
 *   `model_id` are null on a run written before those fields existed, which is
 *   missing ATTRIBUTION, not a missing model.
 * - `blind`: the brain reported no model. `gap` names which gap — `no_credential`
 *   or `unresolved` — and is null only on a run that named none. That review
 *   settles its check `neutral` and says so; this surface REPORTS that, it does
 *   not re-decide it.
 * - `unknown`: no brain event on the run at all. It is not evidence a model ran
 *   and not evidence none did.
 *
 * Collapsing `unknown` into `blind` is the exact defect an operator view shipped
 * with (5 of 92 runs mis-reported), so the union is carried through to the
 * presenter rather than flattened here.
 */
const ReviewBrainSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('model'),
    provider: z.string().nullable(),
    model_id: z.string().nullable(),
  }),
  z.object({ kind: z.literal('blind'), gap: z.string().nullable() }),
  z.object({ kind: z.literal('unknown') }),
]);
export type ReviewBrain = z.infer<typeof ReviewBrainSchema>;

/**
 * What the box ACTUALLY drove for this run (`run_provenance`), reported SEPARATELY
 * from {@link ReviewBrain} because the two answer different questions: provenance
 * is what ran, the brain verdict is what was resolved.
 *
 * Null on every review run whose box recorded none — which is every historical one
 * (0 of 3,582 `pr_review` tasks in the 30 days to 2026-09-12). That null is
 * INFORMATION and is rendered as such; it is not a placeholder waiting to be
 * deleted, and nothing here infers one field from the other.
 */
const ReviewProvenanceSchema = z.object({
  model: z.string(),
  /** What the provider actually SERVED, off the response body; null on an older box. */
  served_model: z.string().nullable(),
  provider: z.string(),
});
export type ReviewProvenance = z.infer<typeof ReviewProvenanceSchema>;

/** `review.list`'s wire row — snake_case, ISO timestamps, the verdict kept as a verdict. */
export const RepoReviewRunSchema = z.object({
  task_id: z.string(),
  /** Links to `/fleet/runs/:run_id`; null until a box claims and STARTS the review. */
  run_id: z.string().nullable(),
  status: TaskStatusSchema,
  /**
   * NULL when the run's envelope carried no usable number, which is a state the
   * store can produce: `pr_review` rows are jsonb envelopes and `review` is
   * optional on them. Never `0` — the row renders as "unknown pull request" and
   * links nowhere rather than linking to `/pull/0`.
   */
  pr_number: z.number().int().positive().nullable(),
  head_sha: z.string().nullable(),
  incremental: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  brain: ReviewBrainSchema,
  provenance: ReviewProvenanceSchema.nullable(),
  /** Findings the run POSTED. Null is "the run never reached the step", never zero. */
  findings_kept: z.number().nullable(),
  /** `approve` | `comment` | `request_changes`; null until the review posts. */
  verdict: z.string().nullable(),
});
export type RepoReviewRun = z.infer<typeof RepoReviewRunSchema>;

/**
 * The response envelope, PARSED rather than asserted.
 *
 * A bare `apiFetch<T>` is a cast: a `reviews` key that arrived absent or as
 * something other than an array reached `<RepoReviewsTab/>` and died there on
 * `rows.length`, blaming the render for a wire fault. Parsing at the seam the bad
 * value enters turns that into the screen's own error state naming the field —
 * the posture `parseBody`'s header argues for every read (`./api-errors.ts`).
 */
const repoReviewsSchema = z.object({ reviews: z.array(RepoReviewRunSchema) });

export const REPO_REVIEWS_QUERY_KEY = 'repo-reviews';

/** The head of this repo's review runs, newest-first. Idle without a session. */
export function useRepoReviews(encoded: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<readonly RepoReviewRun[]>(() => ({
    queryKey: [REPO_REVIEWS_QUERY_KEY, encoded(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () =>
      (
        await apiFetch(`${repoApiBase(encoded())}/reviews`, token, undefined, {
          schema: repoReviewsSchema,
        })
      ).reviews,
  }));
}

/** The three verdicts the reviewer posts, as i18n keys. Anything else renders verbatim. */
const VERDICT_LABEL: Readonly<Record<string, string>> = {
  approve: 'repo_detail.reviews_verdict_approve',
  comment: 'repo_detail.reviews_verdict_comment',
  request_changes: 'repo_detail.reviews_verdict_request_changes',
};

/**
 * The i18n key for a posted verdict, or `null` when the value is not one this app
 * has copy for — the caller then renders the server's own string.
 *
 * A verdict the dashboard does not know is SHOWN, never swallowed: the wire types
 * it `string` because the value is written by the review lane, and a `⟦key⟧` in
 * its place would hide what the reviewer actually told GitHub.
 */
export function reviewVerdictKey(verdict: string): string | null {
  return VERDICT_LABEL[verdict] ?? null;
}

/** The gap a blind review named, as an i18n key. Total — an unnamed gap has its own line. */
export function reviewBlindGapKey(gap: string | null): string {
  if (gap === 'no_credential') return 'review_brain.blind_gap_no_credential';
  if (gap === 'unresolved') return 'review_brain.blind_gap_unresolved';
  return 'review_brain.blind_gap_unnamed';
}
